import { useEffect, useState } from "react";
import { BUILD_COMMAND, updateState } from "../../../shared/extensionUpdate";
import { isRunning, type CaptureValue } from "../lib/captureContext";

/** How long a reload may take to come back before the page says so. */
const RELOAD_TIMEOUT_MS = 20_000;

type Phase =
  | { kind: "idle" }
  | { kind: "reloading"; from: string }
  | { kind: "done" }
  /** Shown only while the build it was about (`at`) is still the running one. */
  | { kind: "failed"; why: string; at: string };

/**
 * Says when the capture extension is behind, and reloads it from here.
 *
 * Two cases, decided by `updateState`: a different build already on disk, which
 * Reload picks up, and a newer shipped version not built yet, which needs a
 * pull and a build first. Reload is offered only by a build that answers it
 * (`hello.reload`), and not while a run is live; the extension refuses then too.
 */
export function ExtensionUpdate({ capture }: { capture: CaptureValue }) {
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [copied, setCopied] = useState(false);
  const hello = capture.hello;

  // The reload lands when the reconnected worker reports a different build.
  useEffect(() => {
    if (phase.kind !== "reloading") return;
    if (capture.connection === "ready" && hello && (hello.built ?? hello.commit) !== phase.from) {
      setPhase({ kind: "done" });
      return;
    }
    const at = phase.from;
    const timer = setTimeout(() => {
      setPhase({ kind: "failed", at,
        why: "It did not come back. Open the extension's popup to check it." });
    }, RELOAD_TIMEOUT_MS);
    return () => { clearTimeout(timer); };
  }, [phase, capture.connection, hello]);

  if (!hello) return null;

  const running = { version: hello.extension, commit: hello.commit, built: hello.built ?? "" };
  const state = updateState(running, hello.on_disk ?? null, __EXTENSION_LATEST__);
  const key = hello.built ?? hello.commit;

  // "Updated" holds only until something newer appears, so a later build is offered.
  if (phase.kind === "done" && state.kind === "current") {
    return (
      <div role="status" className="flex flex-wrap items-center gap-3 rounded-xl border border-green-900 bg-green-950/30 p-3 text-sm">
        <span className="font-semibold text-green-400">Updated</span>
        <span className="text-neutral-300">
          Now running Points Capture {hello.extension} ({hello.commit}).
        </span>
      </div>
    );
  }
  if (phase.kind === "reloading") {
    return (
      <div role="status" className="flex flex-wrap items-center gap-3 rounded-xl border border-neutral-700 bg-neutral-900 p-3 text-sm">
        <span className="font-semibold text-neutral-200">Reloading Points Capture…</span>
        <span className="text-neutral-400">The connection drops for a moment and comes back on its own.</span>
      </div>
    );
  }

  if (state.kind === "current") return null;

  const live = isRunning(capture);
  const canReload = hello.reload === true;

  async function reload() {
    const from = key;
    setPhase({ kind: "reloading", from });
    const answer = await capture.reload();
    // Only a refusal is final. Any other failure may be the port dropping
    // because the reload went through, so the reconnect or the timeout decides.
    if ("error" in answer && answer.error === "busy") {
      setPhase({ kind: "failed", at: from, why: "A run is going. Reload once it finishes." });
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(BUILD_COMMAND);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  const failure = phase.kind === "failed" && phase.at === key
    ? <p className="w-full text-[13px] text-amber-400">{phase.why}</p>
    : null;

  if (state.kind === "ready") {
    return (
      <div role="status" className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-900 bg-amber-950/30 p-3 text-sm">
        <span className="font-semibold text-amber-400">Update ready</span>
        <span className="flex-[1_1_260px] text-neutral-300">
          Points Capture {state.onDisk.version} is built. This browser is still running {hello.extension}.
          {live ? " Reload once this run finishes." : ""}
          {!canReload ? " Reload it once from the extension's popup; after that it can be done here." : ""}
        </span>
        {canReload && (
          <button type="button" onClick={() => void reload()} disabled={live} className="btn btn-primary btn-sm ml-auto">
            Reload extension
          </button>
        )}
        {failure}
      </div>
    );
  }

  return (
    <div role="status" className="flex flex-col gap-2.5 rounded-xl border border-amber-900 bg-amber-950/30 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-semibold text-amber-400">Update available</span>
        <span className="flex-[1_1_260px] text-neutral-300">
          Points Capture {state.latest} is out and this browser runs {hello.extension}. A reload alone
          won't pick it up: build it, then {canReload ? "reload here" : "reload it from the extension's popup"}.
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-[1_1_320px] overflow-x-auto whitespace-nowrap rounded-lg border border-neutral-800 bg-neutral-950 px-3 py-2 text-xs text-neutral-200">
          {BUILD_COMMAND}
        </code>
        <button type="button" onClick={() => void copy()} className="btn btn-ghost btn-sm">
          {copied ? "Copied" : "Copy"}
        </button>
        <button type="button" onClick={capture.recheck} className="btn btn-ghost btn-sm">
          Check again
        </button>
      </div>
      {failure}
    </div>
  );
}
